import React, { useCallback, useEffect, useMemo, useState } from 'react';
import HotTopicItem from './HotTopicItem';
import { injectIntl } from 'react-intl';
import AppContentContainer from '../../../../components/AppContentContainer/AppContentContainer.js';
import { usePrevious } from '../../../../utils/hooks';
import { useSelector } from 'react-redux';
import TagsRegion from './TagsRegion/TagsRegion';
import VideoCard from '../../../../components/VideoCard/VideoCard';
import { useWidth } from '../../../../utils/hooks';
import NativeOrWeb from '../../../../utils/native';
import { isSigningClassroomIframe, isSigningClassroomPath } from '../../../../utils/is-embedded-iframe';
import { tracking } from '../../../../utils/tracking';

const RowStyle: React.CSSProperties = { flex: 1, display: 'flex', alignItems: 'flex-start' };
const TitleStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 15px'
};
const WrapperStyle: React.CSSProperties = { paddingTop: 8 };
const ItemStyle = (
  isLargeScreen: boolean,
  rowItemLength?: number,
  studioMode?: boolean
): React.CSSProperties => {
  // Studio covers are wider than classic thumbs; on mobile use content-sized
  // cells and let the row centre them so a single pack isn't stuck on the left.
  if (studioMode && !isLargeScreen) {
    return {
      width: 'auto',
      display: 'flex',
      justifyContent: 'center',
      padding: '8px 4px'
    };
  }
  return {
    width: isLargeScreen ? '20%' : rowItemLength ? `${100 / rowItemLength}%` : '33%',
    display: 'flex',
    justifyContent: 'center'
  };
};

const getStudioTemplateId = (item: any) => String((item && item.id) || '').replace(/^featured-/, '');

const HotTopicSeeMoreCategory: React.FC<any> = ({ location, history, intl, forceStudioMode = false }) => {
  const [contents, setContents] = useState<any>(null);
  const [className, setClassName] = useState<string | undefined>(undefined);
  const [rowItemLength, setRowItemLength] = useState<number | undefined>(undefined);
  const [statistics, setStatistics] = useState<any>(undefined);
  const [tag, setTag] = useState<any>(undefined);
  const [taggedContents, setTaggedContents] = useState<any>(undefined);
  const [studioMode, setStudioMode] = useState(
    !!forceStudioMode || (location.pathname || '').startsWith('/hot_topic_studio_')
  );
  const [tagViewPath, setTagViewPath] = useState(
    forceStudioMode || (location.pathname || '').startsWith('/hot_topic_studio_')
      ? '/hot_topic_studio_tag'
      : undefined
  );

  const userDetails = useSelector((state: any) => state.user.userDetails);
  const subscriptions = useSelector((state: any) => state.userSubscription.subscriptions);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 850);
  const [isIpadWidth, setIsIpadWidth] = useState(window.innerWidth > 767);
  const previousIsLargeScreen = usePrevious(isLargeScreen);
  const [useCardRender, setUseCardRender] = useState<any>(false);

  const w = useWidth();
  const isMD = w === 'md';
  const isMobile = w === 'xs' || w === 'sm';

  useEffect(() => {
    const pathname = location.pathname || '';
    if (!isSigningClassroomPath(pathname)) {
      return;
    }
    const isIOSNative = NativeOrWeb.isIOSPlatform();
    const isUserAuthenticated = userDetails && userDetails._id;
    // Same contract as NewHelpCenter: iframe / iOS native keep the list; web guests bounce.
    if (!isSigningClassroomIframe() && !isIOSNative && !isUserAuthenticated) {
      history.push({ pathname: '/login', state: { from: pathname } });
    }
  }, [userDetails, location.pathname, history]);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 850);
      setIsIpadWidth(window.innerWidth > 767);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const isStudioPath = (location.pathname || '').startsWith('/hot_topic_studio_');
    if (location.state) {
      if (location.state.contents) {
        setContents(location.state.contents);
      }
      if (location.state.className) {
        setClassName(location.state.className);
      }
      if (location.state.rowItemLength) {
        setRowItemLength(location.state.rowItemLength);
      }
      if (location.state.statistics) {
        setStatistics(location.state.statistics);
      }
      if (location.state.tag) {
        setTag(location.state.tag);
      }
      if (location.state.taggedContents) {
        setTaggedContents(location.state.taggedContents);
      } else {
        setTaggedContents(undefined);
      }
      if (location.state.useCardRender) {
        setUseCardRender(location.state.useCardRender);
      }
      if (location.state.tagViewPath) {
        setTagViewPath(location.state.tagViewPath);
      } else if (forceStudioMode || isStudioPath) {
        setTagViewPath('/hot_topic_studio_tag');
      }
      setStudioMode(!!forceStudioMode || isStudioPath || !!location.state.studioMode);
    } else if (forceStudioMode || isStudioPath) {
      setStudioMode(true);
      setTagViewPath('/hot_topic_studio_tag');
    }
  }, [location, forceStudioMode]);

  const contentArray = useMemo(() => {
    if (!contents) {
      return [];
    }
    const result: any[][] = [];
    // Studio mobile covers need more room — 2 per row instead of 3.
    const itemsPerRow = isLargeScreen ? 5 : studioMode ? rowItemLength || 2 : rowItemLength || 3;
    for (let i = 0; i < contents.items.length; i += itemsPerRow) {
      result.push(contents.items.slice(i, i + itemsPerRow));
    }
    return result;
  }, [contents, isLargeScreen, rowItemLength, studioMode]);

  const handleStudioItemClick = useCallback(
    (item: any) => {
      const templateId = getStudioTemplateId(item);
      if (!templateId) {
        return;
      }
      tracking('Click on Hot Topic Studio Template', { id: templateId });
      history.push(`/hot_topic_studio/${templateId}`);
    },
    [history]
  );

  const itemProps = useMemo(() => {
    const baseProps: any = { enableLanguages: true, ...((location.state && location.state.HotTopicItemProps) || {}) };
    // Never keep functions from location.state — history state must be serializable.
    delete baseProps.onItemClick;
    if (studioMode) {
      return {
        ...baseProps,
        showStudioCover: true,
        onItemClick: handleStudioItemClick
      };
    }
    return baseProps;
  }, [location.state, studioMode, handleStudioItemClick]);

  const onClickTag = ({ tag: nextTag, items }: { tag: any; items: any[] }) => {
    history.push({
      pathname: tagViewPath || (location.state && location.state.tagViewPath) || '/hot_topic_tag',
      state: {
        taggedContents: taggedContents,
        tag: nextTag,
        contents: {
          categoryName: nextTag,
          items
        },
        useCardRender: useCardRender,
        statistics: statistics,
        studioMode,
        tagViewPath: tagViewPath || (location.state && location.state.tagViewPath) || '/hot_topic_tag',
        HotTopicItemProps: {
          enableLanguages: true,
          ...(studioMode ? { showStudioCover: true } : {})
        }
      }
    });
  };

  return (
    <AppContentContainer className={className}>
      {taggedContents && (
        <TagsRegion
          taggedContents={taggedContents.map((content: any) => {
            return {
              ...content,
              hidden: content.tag === tag
            };
          })}
          onClickTag={onClickTag}
        />
      )}
      <div className="hot-topic">
        {contents && (
          <div className="row">
            <div className="col-lg-12" style={WrapperStyle}>
              <div style={TitleStyle}>
                <div className="hot-topic-category-title">{contents.categoryName[intl.locale]}</div>
              </div>
              <div>
                {contentArray.map((e, index) => (
                  <div
                    key={`hot-topic-row-${index}`}
                    style={{
                      ...RowStyle,
                      ...(studioMode && !isLargeScreen
                        ? { justifyContent: 'center', flexWrap: 'wrap' }
                        : {}),
                      ...(useCardRender === 'video_card' ? { marginRight: 8 } : {})
                    }}
                  >
                    {e.map(item => {
                      const statisticId = item.id?.startsWith('featured-')
                        ? item.id.replace('featured-', '')
                        : item.id;

                      return (
                        <div
                          key={`hot-topic-row-${index}-item-dialog-${item.id}`}
                          style={ItemStyle(isLargeScreen, rowItemLength, studioMode)}
                        >
                          <HotTopicItem
                            cardRender={
                              useCardRender === 'video_card'
                                ? (itemData: any, onClick: any) => {
                                    return (
                                      <div>
                                        <VideoCard
                                          {...({
                                            ContainerProps: {
                                              style: {
                                                width: isMD ? '168px' : '200px',
                                                maxWidth: isMobile ? '44vw' : undefined
                                              }
                                            },
                                            type: 'portrait',
                                            itemData,
                                            locale: intl.locale,
                                            intl,
                                            onClick
                                          } as any)}
                                        />
                                      </div>
                                    );
                                  }
                                : undefined
                            }
                            toggleDialogShowTags={useCardRender === 'video_card'}
                            intl={intl}
                            itemData={item}
                            index={0}
                            history={history}
                            isLargeScreen={isLargeScreen}
                            previousIsLargeScreen={previousIsLargeScreen}
                            userDetails={userDetails}
                            subscriptions={subscriptions}
                            isIpadWidth={isIpadWidth}
                            shouldShowStatistic={!!statistics}
                            statistic={statistics ? statistics[statisticId] : undefined}
                            {...itemProps}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppContentContainer>
  );
};

export default injectIntl(HotTopicSeeMoreCategory);
